import React from 'react'
import { useEffect, useState, useCallback, useContext } from 'react'
import { useNavigate, useBlocker } from 'react-router'
import { Dialog, useToaster } from '@enterprise-ui/canvas-ui-react'
import { useAnalytics } from '@praxis/component-analytics'
import { SectionHeader } from '../../globalComponents/SectionHeader'
import { DataTable } from '../../globalComponents/DataTable'
import {
  TOASTER_DEFAULTS,
  COST_SEARCH_DEFAULT_SELECTIONS,
  TOO_MANY_RECORDS,
  COST_SEARCH_VIEW_BY_OPTIONS,
  trainingLinkUrl,
} from '../../globalConfig/constants'
import { CostSearch } from './CostSearch'
import uniq from 'lodash/uniq'
import { VendorContext } from '../../globalState/Vendor/index'
import { SaveViewModal } from '../SavedView/SaveViewModal'
import fileDownload from 'js-file-download'
import {
  downloadSearchedCosts,
  getSavedViewsMetadata,
  saveView,
  searchCosts,
} from '../../services/requests'
import { SavedViewContext } from '../../globalState/SavedViews'
import { CostSearchContext } from '../../globalState/CostSearch'
import isEqual from 'lodash/isEqual'

export const CostSearchPage = () => {
  const { trackView } = useAnalytics()
  const navigate = useNavigate()
  const makeToast = useToaster()
  const [costSearchCriteriaState, costSearchCriteriaDispatch] =
    useContext(CostSearchContext)
  const [searchResults, setSearchResults] = useState(null)
  const [currentlySelectedCriteria, setCurrentlySelectedCriteria] =
    useState(null)
  const [waitingOnSearchResults, setWaitingOnSearchResults] = useState(false)
  const [waitingOnSaveView, setWaitingOnSaveView] = useState(false)
  const [renderSaveViewPopup, setRenderSaveViewPopup] = useState(false)
  const [waitingOnDownload, setWaitingOnDownload] = useState(false)
  const [showPendingPopup, setShowPendingPopup] = useState(false)
  const [changeCount, setChangeCount] = useState(0)
  const [state] = React.useContext(VendorContext)
  // eslint-disable-next-line no-unused-vars
  const [savedViewState, savedViewDispatch] = React.useContext(SavedViewContext)

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      changeCount > 0 && currentLocation.pathname !== nextLocation.pathname,
  )

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowPendingPopup(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker])

  const makeCostSearchRequest = useCallback(
    (vendor, selections) => {
      const sendFailureToast = () => {
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'error',
          heading: 'Error',
          message: 'Failed to get search results',
        })
      }
      const tooManyRecordsToast = () => {
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'error',
          heading: 'Error',
          message:
            'Too many records requested. Narrow your filters and try again.',
        })
      }
      const constructRequestBody = (selections) => {
        const returnObj = { ...selections }
        delete returnObj.searchAfterChanges
        return returnObj
      }

      setWaitingOnSearchResults(true)
      const requestBody = {
        ...constructRequestBody(selections),
        vendorId: parseInt(vendor),
      }
      setCurrentlySelectedCriteria(requestBody)
      searchCosts(vendor, requestBody)
        .then((response) => {
          setWaitingOnSearchResults(false)
          if (response?.status === 200) {
            setSearchResults({ ...response?.data, selections: requestBody })
          } else {
            if (response?.data?.errorCode === TOO_MANY_RECORDS) {
              tooManyRecordsToast()
              setSearchResults(null)
            } else {
              sendFailureToast()
              setSearchResults(null)
            }
          }
        })
        .catch((e) => {
          setWaitingOnSearchResults(false)
          if (e?.response?.data?.errorCode === TOO_MANY_RECORDS) {
            tooManyRecordsToast()
            setSearchResults(null)
          } else {
            sendFailureToast()
            setSearchResults(null)
          }
        })
    },
    [makeToast],
  )

  useEffect(() => {
    trackView('/cost-search', 'Cost Search Page')
  }, [trackView])

  useEffect(() => {
    if (searchResults) {
      setSearchResults(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedVendor])

  useEffect(() => {
    if (costSearchCriteriaState.costSearchCriteria.searchAfterChanges) {
      handleSearchClick()
      costSearchCriteriaDispatch({
        type: 'update_cost_search_criteria',
        costSearchCriteria: {
          ...costSearchCriteriaState.costSearchCriteria,
          searchAfterChanges: false,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costSearchCriteriaState.costSearchCriteria?.searchAfterChanges])

  const setNewSearchSelections = (
    newSearchSelectionValues,
    makeSearchRequestAfterAdding,
  ) => {
    const concatenatedArrays = {}
    Object.keys(newSearchSelectionValues).forEach((selectionKey) => {
      concatenatedArrays[selectionKey] = Array.isArray(
        costSearchCriteriaState.costSearchCriteria[selectionKey],
      )
        ? uniq(
            costSearchCriteriaState.costSearchCriteria[selectionKey].concat(
              newSearchSelectionValues[selectionKey],
            ),
            false,
          )
        : newSearchSelectionValues[selectionKey]
    })
    costSearchCriteriaDispatch({
      type: 'update_cost_search_criteria',
      costSearchCriteria: {
        ...costSearchCriteriaState.costSearchCriteria,
        ...concatenatedArrays,
        searchAfterChanges: makeSearchRequestAfterAdding,
      },
    })
  }

  const removeSelection = useCallback(
    (item, field, values, type) => {
      var tempList
      if (type) {
        tempList = []
      } else {
        tempList = values
        var removeObj = tempList.findIndex((obj) => obj === item)
        tempList.splice(removeObj, 1)
      }

      if (
        Object.values(COST_SEARCH_VIEW_BY_OPTIONS).find(
          (obj) => obj.variableName === field,
        )
      ) {
        costSearchCriteriaDispatch({
          type: 'update_cost_search_criteria',
          costSearchCriteria: {
            ...costSearchCriteriaState.costSearchCriteria,
            costLocationGroup: {
              costLocationGroupType: field,
              costLocationGroupValues: tempList,
            },
          },
        })
      } else {
        costSearchCriteriaDispatch({
          type: 'update_cost_search_criteria',
          costSearchCriteria: {
            ...costSearchCriteriaState.costSearchCriteria,
            [field]: tempList,
          },
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [costSearchCriteriaState.costSearchCriteria],
  )

  const handleSaveView = (saveViewName) => {
    const sendFailureToast = () => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message: 'Failed to save view',
      })
    }

    let savedViewForm = {
      viewId: null,
      userId: null,
      title: saveViewName,
      vendorId: parseInt(state?.selectedVendor),
      searchRequest: currentlySelectedCriteria,
    }

    setWaitingOnSaveView(true)
    saveView(parseInt(state?.selectedVendor), savedViewForm)
      .then((response) => {
        setRenderSaveViewPopup(false)
        setWaitingOnSaveView(false)
        if (response?.status === 200) {
          makeToast({
            ...TOASTER_DEFAULTS,
            type: 'success',
            heading: 'Success',
            message:
              'Saved view "' + saveViewName + '" has been successfuly saved.',
          })
          navigate('/saved-view/' + response?.data?.viewId)
        } else {
          sendFailureToast()
        }
      })
      .catch(() => {
        setRenderSaveViewPopup(false)
        setWaitingOnSaveView(false)
        sendFailureToast()
      })
      .finally(() => {
        getSavedViewMetadata()
      })
  }

  const getSavedViewMetadata = () => {
    getSavedViewsMetadata(parseInt(state?.selectedVendor))
      .then((response) => {
        if (response?.status === 200) {
          savedViewDispatch({
            type: 'update_saved_views',
            savedViews: response?.data,
          })
        }
      })
      .catch(() => {
        makeToast({
          type: 'error',
          heading: 'Error',
          message: 'Failed to retrieve Saved Views',
        })
      })
  }

  const handleSearchClick = () => {
    if (changeCount > 0) {
      setShowPendingPopup(true)
    } else {
      makeCostSearchRequest(
        state.selectedVendor,
        costSearchCriteriaState.costSearchCriteria,
      )
    }
  }

  const handleResetSelectionsClick = () => {
    costSearchCriteriaDispatch({
      type: 'update_cost_search_criteria',
      costSearchCriteria: COST_SEARCH_DEFAULT_SELECTIONS,
    })
  }

  const handleSaveViewOpen = () => {
    setRenderSaveViewPopup(true)
  }

  const handleSaveViewClose = () => {
    setRenderSaveViewPopup(false)
  }

  const makeDownloadRequest = () => {
    setWaitingOnDownload(true)
    downloadSearchedCosts(
      searchResults?.selections?.vendorId,
      searchResults?.selections,
    )
      .then((response) => {
        setWaitingOnDownload(false)
        const fileName =
          `Cost Search Results ${new Date().toLocaleString()}.xlsx`
            .replaceAll('/', '-')
            .replaceAll(':', '_')
        fileDownload(response.data, fileName)
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'success',
          heading: 'Download Successful',
          message: `"${fileName}" successfully downloaded`,
        })
      })
      .catch(() => {
        setWaitingOnDownload(false)
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'error',
          heading: 'Error',
          message: 'Failed to download search results',
        })
      })
  }

  const getChangeCount = (count) => {
    setChangeCount(count)
  }

  const headerSubtext = (
    <>
      <span>
        {`Use this page to search and update a vendor's cost records.
        Note that any submitted updates will not be effective until they are approved by Target.
        Visit our `}
      </span>
      <a
        href={trainingLinkUrl}
        target="_blank"
        className="hesp-link"
        rel="noreferrer"
      >
        training materials
      </a>
      <span>
        {` to learn more about how to most effectively use this page.`}
      </span>
    </>
  )

  return (
    <>
      <SectionHeader
        className="hesp-sectionHeader-layout"
        pageHeading="Cost Search"
        subtext={headerSubtext}
      />
      <CostSearch
        addNewSelections={setNewSearchSelections}
        onSearchClick={handleSearchClick}
        onResetSelectionsClick={handleResetSelectionsClick}
        activeSearchSelections={costSearchCriteriaState.costSearchCriteria}
        vendorId={state.selectedVendor}
        premadeSelections={
          isEqual(
            costSearchCriteriaState.costSearchCriteria,
            COST_SEARCH_DEFAULT_SELECTIONS,
          )
            ? undefined
            : costSearchCriteriaState.costSearchCriteria
        }
        removeValue={removeSelection}
        loading={waitingOnSearchResults}
        isExpanded={true}
      />
      <DataTable
        tableData={searchResults}
        loading={waitingOnSearchResults}
        onDownload={makeDownloadRequest}
        waitingOnDownload={waitingOnDownload}
        onSaveViewOpen={handleSaveViewOpen}
        sendChangeCount={getChangeCount}
      />
      <SaveViewModal
        renderPopup={renderSaveViewPopup}
        modalOnClick={handleSaveView}
        waitingOnSaveViewModal={waitingOnSaveView}
        onClose={handleSaveViewClose}
        modalType="Save"
        saveViewName=""
      />
      <Dialog
        data-testid="unsubmitted-popup"
        bodyText="You have unsubmitted cost changes, are you sure you want to continue?"
        headingText="Unsubmitted Changes"
        isVisible={showPendingPopup}
        refuseButtonText="Finish Submitting"
        onRefuse={() => {
          setShowPendingPopup(false)
          if (blocker.state === 'blocked') {
            blocker.reset()
          }
        }}
        approveButtonText="Continue Without Submitting"
        onApprove={() => {
          setShowPendingPopup(false)
          if (blocker.state === 'blocked') {
            navigate(blocker.location.pathname)
            blocker.proceed()
          } else {
            makeCostSearchRequest(
              state.selectedVendor,
              costSearchCriteriaState.costSearchCriteria,
            )
          }
        }}
      />
    </>
  )
}
